import { Box, Flex, Tooltip } from '@chakra-ui/react';
import { useMemo, memo } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { KText } from '@/ui-library/components/text';
import { KButton } from '@/ui-library/components/atoms/KButton';
import { KAutoComplete } from '@/ui-library/components/organisms/KFields/KAutoComplete';

import { Requisition } from '@/components/shared/requisitions/associate-req/associateReq.interface';
import { AssociatedReqListTable } from '@/components/shared/requisitions/associate-req/components/AssociatedReqListTable';
import { useAtsPermissions } from '@/hooks/useAtsPermissions';
import { RequisitionOption } from '@/components/shared/requisitions/associate-req/components/RequisitionOption';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { filterAndSortRequisitionOptions } from '@/components/shared/requisitions/associate-req/hooks/useRequisitionsOptions';

const IndicatorSeparator = () => null;

interface RequisitionsDisplayProps {
  multiSelect: boolean;
  isFieldRequired: boolean;
  userHasReqEnabled: boolean;
  shouldUseLegacyFlow: boolean;

  hasSelectedRequisitions: boolean;
  selectedRequisitions: Requisition[];

  requisitionsOptions: any[];
  selectedRequisitionsOptionsFromForm: any;
  isLoadingRequisitions: boolean;
  showValidationError: boolean;

  buttonText: string;

  onOpenModalForButton: () => void;
  onOpenModalForSelectField: () => void;
  onSelectChange: (selectedOptions: any) => void;
  onDeleteRequisition: (requisition: Requisition) => void;
  isDeleting: boolean;
  isTableUpdating?: boolean;
  buttonProps?: any;
  offerReq?: any;
}

// Memoized sub-components to prevent re-renders
const RequisitionsLabel = memo(
  ({ isFieldRequired }: { isFieldRequired: boolean }) => (
    <Flex alignItems="center" gap="4px">
      <KText size="sm-semi-bold" color="secondary-900">
        Requisitions
      </KText>
      {isFieldRequired && <KText color="error-100">*</KText>}
    </Flex>
  )
);

RequisitionsLabel.displayName = 'RequisitionsLabel';

const AssociateButton = memo(
  ({
    onClick,
    disabled,
    buttonText,
    buttonProps,
  }: {
    onClick: () => void;
    disabled: boolean;
    buttonText: string;
    buttonProps?: any;
  }) => (
    <KButton
      variant="secondary"
      onClick={onClick}
      disabled={disabled}
      width="fit-content"
      {...buttonProps}
    >
      {buttonText}
    </KButton>
  )
);

AssociateButton.displayName = 'AssociateButton';

const RequisitionSelectField = memo(
  ({
    multiSelect,
    requisitionsOptions,
    selectedRequisitionsOptionsFromForm,
    onSelectChange,
    showValidationError,
    onOpenModalForSelectField,
    isSelectDisabled,
    isButtonDisabled,
    customComponents,
  }: {
    multiSelect: boolean;
    requisitionsOptions: any[];
    selectedRequisitionsOptionsFromForm: any;
    offerReqOption: any[];
    onSelectChange: (selectedOptions: any) => void;
    showValidationError: boolean;
    isLoadingRequisitions: boolean;
    onOpenModalForSelectField: () => void;
    isSelectDisabled: boolean;
    isButtonDisabled: boolean;
    customComponents: any;
  }) => {
    const { jobId, offerId } = useParams();
    const currUser = useSelector(selectCurrUser);

    // Determine the correct field name based on context
    const fieldName = 'requisitions_codes';

    // Combine selected options with requisitions options so useAsyncSelect can find them
    // This allows the field to match Formik codes with option objects
    const allStaticOptions = useMemo(() => {
      const selected = selectedRequisitionsOptionsFromForm
        ? Array.isArray(selectedRequisitionsOptionsFromForm)
          ? selectedRequisitionsOptionsFromForm
          : [selectedRequisitionsOptionsFromForm]
        : [];

      // Combine and deduplicate by value
      const combined = [...selected, ...requisitionsOptions];
      const unique = combined.filter(
        (option, index, self) =>
          index === self.findIndex((o) => o.value === option.value)
      );

      return unique;
    }, [selectedRequisitionsOptionsFromForm, requisitionsOptions]);

    const autoCompleteItem = {
      name: fieldName,
      isMulti: multiSelect,
      placeholder: 'Search and select requisition',
      disabled: isSelectDisabled,
      components: customComponents,
      menuPlacement: 'auto' as const,
      valueKey: 'code',
      labelKey: 'label',
      loadDefaultOptions: false,
      defaultOptions: allStaticOptions,
      staticOptions: allStaticOptions,
      customOnChange: onSelectChange,
      initialSelectedOption: selectedRequisitionsOptionsFromForm,
      useInput: true,
      initialSearchQuery: '',
      fetchConfig: {
        endPoint: '/requisitions?q=',
        method: 'get',
        httpType: 'ats',
        processResponse: (res: any) => {
          const requisitions = res?.data?.requisitions || [];

          // Transform requisitions to options
          const baseOptions = requisitions.map((req: any) => ({
            value: req.id,
            label: `${req.code} - ${req.role_name}`,
            status: req.status,
            department: req.ats_department?.name,
            locations: req.ats_offices?.map((office: any) => office.name) || [],
            ats_job: req.ats_job,
            requisition_association: req.requisition_association,
            requisition_type: req.requisition_type,
            confidential: req.confidential,
            requisition_group_id: req.requisition_group_id,
            group_size: req.group_size,
            code: req.code,
            recruiter: req.recruiter,
            allRequisitions: requisitions,
          }));

          // Apply filtering and sorting using shared utility function
          return filterAndSortRequisitionOptions(baseOptions, {
            multiSelect,
            jobId,
            offerId,
            currUserId: currUser?.id,
          });
        },
        payload: {
          params: {
            sort_key: 'created_at',
            sort_order: 'desc',
            page: 1,
            limit: 200,
            status: multiSelect
              ? {
                  is: [
                    'approved',
                    'created',
                    'open',
                    'draft',
                    'pending_approval',
                    'rejected',
                  ],
                }
              : { is: ['approved', 'created', 'open', 'filled'] },
            exclude_associated: multiSelect,
          },
        },
      },
      customNoOptionsMessage: () => (
        <Box p="8px">
          <KText size="sm-regular" color="secondary-600">
            No requisitions found
          </KText>
        </Box>
      ),
    };

    return (
      <Flex gap="10px" flexDirection="column" w="100%">
        <Box w="100%" maxW="100%">
          <KAutoComplete
            item={autoCompleteItem}
            formik={{ submitCount: showValidationError ? 1 : 0 }}
          />
        </Box>
        <Tooltip
          label={
            requisitionsOptions.length === 0
              ? 'No requisitions available to associate'
              : ''
          }
          hasArrow={true}
          placement="bottom"
        >
          <Box w="fit-content">
            <KButton
              variant="secondary"
              onClick={onOpenModalForSelectField}
              disabled={isButtonDisabled}
              width="fit-content"
            >
              See all requisitions
            </KButton>
          </Box>
        </Tooltip>
      </Flex>
    );
  }
);

RequisitionSelectField.displayName = 'RequisitionSelectField';

const MultiSelectView = memo(
  ({
    userHasReqEnabled,
    hasSelectedRequisitions,
    isFieldRequired,
    onOpenModalForButton,
    onOpenModalForSelectField,
    multiSelect,
    canUpdate,
    buttonText,
    buttonProps,
    requisitionsOptions,
    selectedRequisitionsOptionsFromForm,
    offerReqOption,
    onSelectChange,
    showValidationError,
    isLoadingRequisitions,
    canOffer,
    selectedRequisitions,
    onDeleteRequisition,
    isDeleting,
    isTableUpdating,
    customComponents,
  }: {
    userHasReqEnabled: boolean;
    hasSelectedRequisitions: boolean;
    isFieldRequired: boolean;
    onOpenModalForButton: () => void;
    onOpenModalForSelectField: () => void;
    multiSelect: boolean;
    canUpdate: boolean;
    buttonText: string;
    buttonProps?: any;
    requisitionsOptions: any[];
    selectedRequisitionsOptionsFromForm: any;
    offerReqOption: any[];
    onSelectChange: (selectedOptions: any) => void;
    showValidationError: boolean;
    isLoadingRequisitions: boolean;
    canOffer: boolean;
    selectedRequisitions: Requisition[];
    onDeleteRequisition: (requisition: Requisition) => void;
    isDeleting: boolean;
    isTableUpdating: boolean;
    customComponents: any;
  }) => {
    const showSearchSelect = userHasReqEnabled && !hasSelectedRequisitions;
    const showAssociateButton = userHasReqEnabled && hasSelectedRequisitions;
    const isSelectDisabled =
      isLoadingRequisitions || (multiSelect ? !canUpdate : !canOffer);
    const isButtonDisabled = multiSelect
      ? !canUpdate || requisitionsOptions.length === 0
      : !canOffer || offerReqOption.length === 0;

    return (
      <>
        <Flex
          direction={hasSelectedRequisitions ? 'row' : 'column'}
          justifyContent={
            hasSelectedRequisitions ? 'space-between' : 'flex-start'
          }
          alignItems={hasSelectedRequisitions ? 'center' : 'flex-start'}
          gap={hasSelectedRequisitions ? '0' : '10px'}
        >
          <RequisitionsLabel isFieldRequired={isFieldRequired} />
          {showAssociateButton && (
            <AssociateButton
              onClick={onOpenModalForButton}
              disabled={isButtonDisabled}
              buttonText={buttonText}
              buttonProps={buttonProps}
            />
          )}
          {showSearchSelect && (
            <RequisitionSelectField
              multiSelect={multiSelect}
              requisitionsOptions={requisitionsOptions}
              selectedRequisitionsOptionsFromForm={
                selectedRequisitionsOptionsFromForm
              }
              offerReqOption={offerReqOption}
              onSelectChange={onSelectChange}
              showValidationError={showValidationError}
              isLoadingRequisitions={isLoadingRequisitions}
              onOpenModalForSelectField={onOpenModalForSelectField}
              isSelectDisabled={isSelectDisabled}
              isButtonDisabled={isButtonDisabled}
              customComponents={customComponents}
            />
          )}
        </Flex>
        {hasSelectedRequisitions && (
          <AssociatedReqListTable
            requisitions={selectedRequisitions}
            onDelete={onDeleteRequisition}
            isDeleting={isDeleting}
            isUpdating={isTableUpdating}
          />
        )}
      </>
    );
  }
);

MultiSelectView.displayName = 'MultiSelectView';

const SingleSelectView = memo(
  ({
    isFieldRequired,
    multiSelect,
    requisitionsOptions,
    selectedRequisitionsOptionsFromForm,
    offerReqOption,
    onSelectChange,
    showValidationError,
    isLoadingRequisitions,
    onOpenModalForSelectField,
    canOffer,
    customComponents,
  }: {
    isFieldRequired: boolean;
    multiSelect: boolean;
    requisitionsOptions: any[];
    selectedRequisitionsOptionsFromForm: any;
    offerReqOption: any[];
    onSelectChange: (selectedOptions: any) => void;
    showValidationError: boolean;
    isLoadingRequisitions: boolean;
    onOpenModalForSelectField: () => void;
    canOffer: boolean;
    customComponents: any;
  }) => {
    const isSelectDisabled = isLoadingRequisitions || !canOffer;
    const isButtonDisabled = !canOffer;
    return (
      <Flex direction="column" gap="10px">
        <RequisitionsLabel isFieldRequired={isFieldRequired} />
        <RequisitionSelectField
          multiSelect={multiSelect}
          requisitionsOptions={requisitionsOptions}
          selectedRequisitionsOptionsFromForm={
            selectedRequisitionsOptionsFromForm
          }
          offerReqOption={offerReqOption}
          onSelectChange={onSelectChange}
          showValidationError={showValidationError}
          isLoadingRequisitions={isLoadingRequisitions}
          onOpenModalForSelectField={onOpenModalForSelectField}
          isSelectDisabled={isSelectDisabled}
          isButtonDisabled={isButtonDisabled}
          customComponents={customComponents}
        />
      </Flex>
    );
  }
);

SingleSelectView.displayName = 'SingleSelectView';

export const RequisitionsDisplay = memo((props: RequisitionsDisplayProps) => {
  const { jobPermissions } = useAtsPermissions();
  const {
    multiSelect,
    isFieldRequired,
    userHasReqEnabled,
    shouldUseLegacyFlow,
    hasSelectedRequisitions,
    selectedRequisitions,
    requisitionsOptions,
    selectedRequisitionsOptionsFromForm,
    isLoadingRequisitions,
    showValidationError,
    buttonText,
    onOpenModalForButton,
    onOpenModalForSelectField,
    onSelectChange,
    onDeleteRequisition,
    isDeleting,
    isTableUpdating = false,
    buttonProps,
    offerReq = null,
  } = props;

  // Memoize permission values to prevent re-renders when object reference changes
  const canUpdate = useMemo(
    () => jobPermissions?.update ?? false,
    [jobPermissions?.update]
  );
  const canOffer = useMemo(
    () => jobPermissions?.offer ?? false,
    [jobPermissions?.offer]
  );

  // Memoize offerReqOption calculation
  const offerReqOption = useMemo(() => {
    if (offerReq && offerReq.length > 0) {
      return [
        {
          value: offerReq[0]?.id,
          label: `${offerReq[0]?.code} - ${offerReq[0]?.role_name}`,
          status: offerReq[0]?.status,
          department: offerReq[0]?.ats_department?.name,
          locations:
            offerReq[0]?.ats_offices?.map((office) => office.name) || [],
          ats_job: offerReq[0]?.ats_job,
          requisition_association: offerReq[0]?.requisition_association,
          requisition_type: offerReq[0]?.requisition_type,
          confidential: offerReq[0]?.confidential,
        },
      ];
    }
    return [];
  }, [offerReq]);

  // Memoize customComponents object
  const customComponents = useMemo(
    () => ({
      Option: RequisitionOption,
      IndicatorSeparator,
    }),
    []
  );

  // Memoize computed values
  const showLegacyButton = useMemo(
    () => !hasSelectedRequisitions && shouldUseLegacyFlow,
    [hasSelectedRequisitions, shouldUseLegacyFlow]
  );

  return (
    <>
      {/* Legacy Flow: Just the associate button (switch to new flow) */}
      {showLegacyButton && (
        <AssociateButton
          onClick={onOpenModalForButton}
          disabled={multiSelect ? !canUpdate : !canOffer}
          buttonText={buttonText}
          buttonProps={buttonProps}
        />
      )}

      {/* Multi-Select Flow: Search/select or table view */}
      {!showLegacyButton && multiSelect && (
        <MultiSelectView
          userHasReqEnabled={userHasReqEnabled}
          hasSelectedRequisitions={hasSelectedRequisitions}
          isFieldRequired={isFieldRequired}
          onOpenModalForButton={onOpenModalForButton}
          onOpenModalForSelectField={onOpenModalForSelectField}
          multiSelect={multiSelect}
          canUpdate={canUpdate}
          buttonText={buttonText}
          buttonProps={buttonProps}
          requisitionsOptions={requisitionsOptions}
          selectedRequisitionsOptionsFromForm={
            selectedRequisitionsOptionsFromForm
          }
          offerReqOption={offerReqOption}
          onSelectChange={onSelectChange}
          showValidationError={showValidationError}
          isLoadingRequisitions={isLoadingRequisitions}
          canOffer={canOffer}
          selectedRequisitions={selectedRequisitions}
          onDeleteRequisition={onDeleteRequisition}
          isDeleting={isDeleting}
          isTableUpdating={isTableUpdating}
          customComponents={customComponents}
        />
      )}

      {/* Single Select Flow - offers : Always show search select and button */}
      {!showLegacyButton && !multiSelect && (
        <SingleSelectView
          isFieldRequired={isFieldRequired}
          multiSelect={multiSelect}
          requisitionsOptions={requisitionsOptions}
          selectedRequisitionsOptionsFromForm={
            selectedRequisitionsOptionsFromForm
          }
          offerReqOption={offerReqOption}
          onSelectChange={onSelectChange}
          showValidationError={showValidationError}
          isLoadingRequisitions={isLoadingRequisitions}
          onOpenModalForSelectField={onOpenModalForSelectField}
          canOffer={canOffer}
          customComponents={customComponents}
        />
      )}

      {/* Validation Error Message */}
      {showValidationError && !shouldUseLegacyFlow && (
        <KText color="error-200" pt="5px">
          Requisitions is required
        </KText>
      )}
    </>
  );
});

RequisitionsDisplay.displayName = 'RequisitionsDisplay';
